/**
 * PercentageChangeInput Component
 *
 * Input component for percentage changes that can be positive or negative
 * Used for fields like "Expenses At Retirement" where values can represent
 * increases (+) or decreases (-) from a baseline.
 */

import React from "react";
import { renderHelpText } from "../utils/helpTextRenderer";

export interface PercentageChangeInputProps {
  /** Field name */
  name: string;
  /** Current value as decimal (e.g., 0.20 for 20%, -0.10 for -10%) */
  value?: number | null;
  /** Handler for value changes */
  onChange: (name: string, value: number) => void;
  /** Handler for blur event (triggers save) */
  onBlur: (name: string, value: number) => void;
  /** Field label */
  label: string;
  /** Optional help text */
  helpText?: string;
  /** Optional placeholder text (defaults to "0") */
  placeholder?: string;
}

/**
 * PercentageChangeInput - Allows positive and negative percentage values
 *
 * Displays value as percentage (e.g., 20% or -10%)
 * Stores value as decimal (e.g., 0.20 or -0.10)
 */
export const PercentageChangeInput: React.FC<PercentageChangeInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  helpText,
  placeholder = "0",
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Allow empty string, minus sign, numbers, and decimal point
    if (inputValue === "" || inputValue === "-" || inputValue === "-.") {
      onChange(name, 0);
      return;
    }

    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue)) {
      // Convert percentage to decimal (e.g., 20 → 0.20, -10 → -0.10)
      onChange(name, numValue / 100);
    }
  };

  const handleBlur = () => {
    onBlur(name, value ?? 0);
  };

  // Convert decimal to percentage for display (e.g., 0.20 → 20, -0.10 → -10)
  // Unset values remain visually blank so placeholder guidance is shown.
  const displayValue =
    value === null || value === undefined ? "" : (value * 100).toString();

  return (
    <div className="form-field">
      <label htmlFor={name} className="form-label">
        {label}
      </label>
      <div className="form-input-wrapper">
        <input
          type="text"
          id={name}
          name={name}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className="percentage-input-with-suffix"
          placeholder={placeholder}
        />
        <span className="input-suffix-unit">%</span>
      </div>
      {helpText && <p className="form-help-text">{renderHelpText(helpText)}</p>}
    </div>
  );
};

export default PercentageChangeInput;
